<template>
  <div class="mb-2">
    <div class="d-flex justify-content-end">
      <b-button
        size="sm"
        variant="flat-primary"
        @click="$emit('close')"
      >
        <feather-icon
          icon="XIcon"
          size="24"
        />
      </b-button>
    </div>
    <b-row>
      <b-col md="8">
        <b-form-group
          label="Fixed Location Address"
          label-for="address"
        >
          <b-form-input
            id="address"
            v-model="address"
            placeholder="Enter an address"
          />
        </b-form-group>

      </b-col>
      <b-col
        md="4"
        class="d-flex justify-content-end align-items-center"
      >
        <b-button
          variant="primary"
          :disabled="submitting"
          @click="submit"
        >
          <b-spinner
            v-if="submitting"
            small
          />
          <span v-else>Save changes</span>
        </b-button>
      </b-col>
      <b-col md="12">
        <v-select
          v-model="selectedPlace"
          placeholder="Start typing to search address"
          label="description"
          :options="places"
          class="mb-2"
          :filterable="false"
          @search="fetchPlaces"
          @input="onSelectedPlace"
        />
      </b-col>
    </b-row>
    <div id="create-fixed-location-map" />
  </div>
</template>

<script>
/* eslint-disable no-undef */
import { onMounted, ref } from '@vue/composition-api'
import {
  BButton, BFormGroup, BFormInput, BRow, BCol, BSpinner,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import { debounce } from 'lodash'
import { errorNotification } from '@/utils/toast-notifications'
import sleep from '@/utils/sleep'
import axiosIns from '@/libs/axios'

export default {
  components: {
    BButton,
    BFormGroup,
    BFormInput,
    BRow,
    vSelect,
    BSpinner,
    BCol,
  },
  props: {
    fixedLocation: {
      type: Object,
      default: undefined,
    },
    branch: {
      type: Object,
      required: true,
    },
  },
  setup(props, ctx) {
    const businessId = ctx.root.$route.params.id
    const map = ref(null)
    const address = ref('')
    const marker = ref(null)
    const registerMapClickListener = () => {
      map.value.addListener('click', ev => {
        if (marker.value) {
          marker.value.setPosition(ev.latLng)
        } else {
          marker.value = new google.maps.Marker({
            position: ev.latLng,
            draggable: true,
          })
          marker.value.setMap(map.value)
          marker.value.addListener('click', () => {
            marker.value.setMap(null)
            marker.value = null
          })
        }
      })
    }
    const initMap = () => {
      const center = {
        lat: 31.963158, lng: 35.930359,
      }
      map.value = new google.maps.Map(document.getElementById('create-fixed-location-map'), {
        zoom: 10,
        center,
      })
      if (props.fixedLocation) {
        address.value = props.fixedLocation.address
        const { lat } = props.fixedLocation
        const lng = props.fixedLocation.lon
        marker.value = new google.maps.Marker({
          draggable: true,
          position: {
            lat,
            lng,
          },
        })
        map.value.setCenter({
          lat,
          lng,
        })
        marker.value.setMap(map.value)
        marker.value.addListener('click', () => {
          marker.value.setMap(null)
          marker.value = null
        })
      }
      registerMapClickListener()
    }
    onMounted(() => {
      initMap()
    })

    const places = ref([])
    const selectedPlace = ref(null)
    const setMarkerLoading = ref(false)
    const fetchPlaces = debounce(async (search, loading) => {
      if (!search) return
      loading(true)
      const res = await axiosIns.get(`/google/search/${search}`)
      places.value = res.data.data.places
      loading(false)
    }, 1000)
    const onSelectedPlace = async () => {
      setMarkerLoading.value = true
      const res = await axiosIns.get(`/google/geocode/${selectedPlace.value.place_id}`)
      const { location } = res.data.data.results.at(0).geometry

      if (marker.value) {
        marker.value.setPosition({
          lat: location.lat,
          lng: location.lng,
        })
        map.value.setCenter({
          lat: location.lat,
          lng: location.lng,
        })
      } else {
        marker.value = new google.maps.Marker({
          draggable: true,
          position: {
            lat: location.lat,
            lng: location.lng,
          },
        })
      }
      marker.value.setMap(map.value)
      if (!address.value) {
        address.value = selectedPlace.value.description
      }
      setMarkerLoading.value = false
    }

    const submitting = ref(false)
    const submit = async () => {
      if (!marker.value?.position?.lat()) {
        errorNotification('Pin a fixed location on the map')
        return
      }
      if (!address.value) {
        errorNotification('Provide an address')
        return
      }
      try {
        submitting.value = true
        const fixedLocationsTemp = []
        if (props.branch.fixedLocations) {
          if (props.fixedLocation) {
            const activeFixedLocationIndex = props.branch.fixedLocations.findIndex(f => f.id === props.fixedLocation.id)
            const tempArr = JSON.parse(JSON.stringify(props.branch.fixedLocations))
            tempArr.splice(activeFixedLocationIndex, 1)
            fixedLocationsTemp.push(...tempArr)
            fixedLocationsTemp.push({
              lat: marker.value.position.lat(),
              lon: marker.value.position.lng(),
              address: address.value,
            })
          } else {
            fixedLocationsTemp.push(...props.branch.fixedLocations)
            fixedLocationsTemp.push({
              lat: marker.value.position.lat(),
              lon: marker.value.position.lng(),
              address: address.value,
            })
          }
        } else {
          fixedLocationsTemp.push({
            lat: marker.value.position.lat(),
            lon: marker.value.position.lng(),
            address: address.value,
          })
        }
        await ctx.root.$store.dispatch('business/updateBusinessBranch', {
          businessId,
          branchId: props.branch.id,
          data: {
            config: {
              fixedLocations: fixedLocationsTemp,
              promos: props.branch.promos || [],
            },
          },
        })
        await sleep(1000)
        ctx.emit('done')
      } finally {
        submitting.value = false
      }
    }

    return {
      submit,
      places,
      selectedPlace,
      submitting,
      address,
      onSelectedPlace,
      fetchPlaces,
    }
  },
}
</script>

<style scoped>
#create-fixed-location-map {
    width:100%;
    height:250px
}
</style>
